'use client';

import { useEffect, useState, useRef } from 'react';
import { useAppStore } from '@/lib/store';
import { translations } from '@/lib/translations';
import { useAuth } from '@/components/auth/AuthProvider';
import { useRouter, usePathname } from 'next/navigation';

const tabs = [
  { id: 'home' as const, icon: 'Home', labelKey: 'home' },
  { id: 'dashboard' as const, icon: 'Layout', labelKey: 'dashboard' },
  { id: 'cr_analysis' as const, icon: 'Target', labelKey: 'crAnalysis' },
  { id: 'radar' as const, icon: 'Hexagon', labelKey: 'radarPlot' },
  { id: 'strategy' as const, icon: 'Target', labelKey: 'strategyFunnel' },
  { id: 'form' as const, icon: 'Plus', labelKey: 'newScore' },
  { id: 'list' as const, icon: 'List', labelKey: 'projects' },
];

const IconWrapper = ({ icon, className }: { icon: string; className?: string }) => {
  const icons: Record<string, () => JSX.Element> = {
    Home: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    Layout: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
        <line x1="3" x2="21" y1="9" y2="9"/>
        <line x1="9" x2="9" y1="21" y2="9"/>
      </svg>
    ),
    Target: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="6"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    Hexagon: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      </svg>
    ),
    List: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" x2="21" y1="6" y2="6"/>
        <line x1="8" x2="21" y1="12" y2="12"/>
        <line x1="8" x2="21" y1="18" y2="18"/>
        <line x1="3" x2="3.01" y1="6" y2="6"/>
        <line x1="3" x2="3.01" y1="12" y2="12"/>
        <line x1="3" x2="3.01" y1="18" y2="18"/>
      </svg>
    ),
    Plus: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14"/>
        <path d="M12 5v14"/>
      </svg>
    ),
    Globe: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" x2="22" y1="12" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
    Settings: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    ),
  };

  const Icon = icons[icon];
  return Icon ? <span className={className}><Icon /></span> : null;
};

export function Navigation() {
  const { activeTab, setActiveTab, lang, toggleLang } = useAppStore();
  const { user, loading, signOut } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const t = translations[lang];
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const mobileDropdownStyle = isMobile
    ? {
        // 避免右侧挖孔/圆角区域遮挡（safe-area-inset-right）
        right: 'calc(env(safe-area-inset-right, 0px) + 1rem)',
        // 避免顶部状态栏遮挡（safe-area-inset-top + 顶部导航栏高度 56px）
        top: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
      }
    : undefined;

  // 检测移动端环境
  useEffect(() => {
    const checkMobile = () => {
      // 使用屏幕宽度检测（768px 是 Tailwind md 断点）
      setIsMobile(window.innerWidth < 768);
    };

    // 初始检测
    checkMobile();

    // 监听窗口大小变化
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const shouldShowAdminButton = !!user && user.role === 'admin' && user.is_active !== false;
    console.log('[Navigation] User state:', {
      loading,
      userId: user?.id,
      role: user?.role,
      is_active: user?.is_active,
      shouldShowAdminButton,
    });
  }, [loading, user?.id, user?.role, user?.is_active]);

  // 点击外部关闭用户菜单
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isUserMenuOpen]);

  const handleTabClick = (tabId: string) => {
    // 调试日志
    console.log('[handleTabClick] tabId:', tabId, 'pathname:', pathname);
    
    // 如果当前在 /admin/users 或其子路径，先导航回主页
    // 使用 startsWith 更健壮地匹配路径
    if (pathname?.startsWith('/admin')) {
      console.log('[handleTabClick] 检测到 admin 页面，正在导航到主页...');
      // 先设置 activeTab，确保状态先更新
      setActiveTab(tabId as any);
      // 然后导航到主页
      router.push('/');
    } else {
      // 正常情况直接更新标签状态
      setActiveTab(tabId as any);
    }
  };

  const handleSignOut = async () => {
    console.log('Sign out button clicked');
    setIsUserMenuOpen(false);
    
    // 立即导航，不等待登出完成
    router.push('/auth/login');
    
    try {
      console.log('Calling signOut function...');
      // 异步执行登出，不阻塞导航
      await signOut();
      console.log('Sign out completed');
    } catch (error) {
      console.error('Sign out error (non-blocking):', error);
      // 即使失败也不影响，因为我们已经导航走了
    }
  };

  // 渲染用户菜单部分（提取为公共部分）
  const renderUserSection = () => (
        <div className="flex items-center gap-1.5 md:gap-3 overflow-visible flex-shrink-0">
      {loading ? (
        <div className="text-sm text-slate-400">加载中...</div>
      ) : user ? (
        <div className="relative overflow-visible" ref={userMenuRef}>
          {/* 用户信息触发按钮 - 移动端简化显示 */}
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-1 md:gap-2 px-1.5 md:px-3 py-1.5 rounded-md text-xs md:text-sm text-slate-600 hover:bg-slate-50 active:bg-slate-100 transition-colors select-none whitespace-nowrap"
          >
            {isMobile ? (
              // 移动端：只显示用户名或邮箱的简化版本
              <span className="font-medium truncate max-w-[80px]">
                {user.name || user.email?.split('@')[0] || '用户'}
              </span>
            ) : (
              // 桌面端：完整信息
              <>
                <div className="text-sm text-slate-600">
                  <span className="font-medium">{user.name || user.email}</span>
                  <span className="text-slate-400 ml-2 hidden lg:inline">
                    ({user.role === 'admin' ? '管理员' : user.role === 'supervisor' ? '监督者' : user.role === 'editor' ? '编辑' : '查看'})
                  </span>
                  {user.is_active === false && (
                    <span className="ml-2 text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded">
                      待激活
                    </span>
                  )}
                </div>
              </>
            )}
            {/* 下拉箭头图标 */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform flex-shrink-0 ${isUserMenuOpen ? 'rotate-180' : ''}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {/* 下拉菜单 */}
          {isUserMenuOpen && (
            <div
              className={`${isMobile ? 'fixed' : 'absolute right-0 top-full mt-2'} w-56 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-[9999]`}
              style={mobileDropdownStyle}
            >
              {/* 用户信息（只读） */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-sm font-medium text-slate-800">{user.name || user.email}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {user.role === 'admin' ? '管理员' : user.role === 'supervisor' ? '监督者' : user.role === 'editor' ? '编辑' : '查看'}
                  {user.is_active === false && (
                    <span className="ml-2 text-yellow-600">待激活</span>
                  )}
                </div>
              </div>

              {/* 菜单项 */}
              <div className="py-1">
                {user.role === 'admin' && (user.is_active !== false) && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      console.log('Navigating to admin users page');
                      router.push('/admin/users');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="19" x2="19" y1="8" y2="14" />
                      <line x1="22" x2="16" y1="11" y2="11" />
                    </svg>
                    用户管理
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    console.log('Navigating to settings page');
                    router.push('/settings');
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2"
                >
                  <IconWrapper icon="Settings" className="w-4 h-4" />
                  设置
                </button>
                {/* 移动端语言切换：放到用户下拉菜单里（避免顶部按钮被挤压/遮挡） */}
                {isMobile && (
                  <button
                    type="button"
                    onClick={() => {
                      toggleLang();
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2"
                  >
                    <IconWrapper icon="Globe" className="w-4 h-4" />
                    {lang === 'en' ? '中文' : 'English'}
                  </button>
                )}
                <div className="border-t border-slate-100 my-1" />
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 active:bg-red-100 flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" x2="9" y1="12" y2="12" />
                  </svg>
                  登出
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => router.push('/auth/login')}
          className="flex items-center gap-1 md:gap-2 px-1.5 md:px-3 py-1.5 rounded-md text-xs md:text-sm text-blue-600 hover:bg-blue-50 active:bg-blue-100 transition-colors select-none whitespace-nowrap flex-shrink-0"
        >
          登录
        </button>
      )}
      {!isMobile && (
        <button
          onClick={toggleLang}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-50 active:bg-slate-100 transition-colors select-none"
        >
          <IconWrapper icon="Globe" className="w-4 h-4" />
          {lang === 'en' ? '中文' : 'English'}
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* 顶部导航栏 */}
      <nav className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50 overflow-visible safe-area-top safe-area-right safe-area-left">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-visible">
          <div className="flex justify-between items-center h-14 md:h-16 overflow-visible gap-2">
            {/* 左侧：标题和标签（桌面端） */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <h1 className="text-base md:text-lg lg:text-xl font-bold text-slate-800 truncate min-w-0">{t.appTitle}</h1>
              {!isMobile && (
                <div className="hidden md:flex gap-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabClick(tab.id)}
                      className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all select-none ${
                        activeTab === tab.id
                          ? 'bg-blue-50 text-blue-600 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <IconWrapper icon={tab.icon} className="w-4 h-4" />
                      {t[tab.labelKey as keyof typeof t]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 右侧：用户菜单和语言切换 */}
            {renderUserSection()}
          </div>
        </div>
      </nav>

      {/* 移动端底部导航栏 */}
      {isMobile && (
        <div 
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-50 safe-area-bottom"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex justify-around items-center h-16">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`relative flex flex-col items-center justify-center gap-1 flex-1 h-full min-h-[64px] transition-all select-none active:bg-slate-50 ${
                  activeTab === tab.id
                    ? 'text-blue-600'
                    : 'text-slate-500'
                }`}
              >
                <IconWrapper icon={tab.icon} className={`w-5 h-5 ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-500'}`} />
                <span className={`text-[10px] font-medium leading-tight ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-500'}`}>
                  {t[tab.labelKey as keyof typeof t]}
                </span>
                {/* 激活状态指示器 */}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-blue-600 rounded-t-full" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

